import { type KeyboardEvent, forwardRef } from 'react';
import { Tag, type TagVariant } from '@/shared/ui/Tag';

export type TagChipVariant = TagVariant;

interface TagChipProps {
  value: string;
  label: string;
  variant?: TagChipVariant;
  /** AI tags show a leading ✦. */
  ai?: boolean;
  /** Render an `×` button. */
  removable?: boolean;
  onRemove?: (value: string) => void;
  /**
   * Forwarded to the remove button — lets the parent handle Backspace on a
   * focused chip without re-implementing the keyboard contract here.
   */
  onRemoveKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
}

const TagChip = forwardRef<HTMLSpanElement, TagChipProps>(
  (
    {
      value,
      label,
      variant = 'default',
      ai,
      removable = false,
      onRemove,
      onRemoveKeyDown,
      disabled = false,
    },
    ref
  ) => (
    <Tag
      ref={ref}
      value={value}
      label={label}
      variant={variant}
      ai={ai}
      onRemove={removable ? onRemove : undefined}
      onRemoveKeyDown={onRemoveKeyDown}
      disabled={disabled}
      role="listitem"
      dataTestId={`tag-chip-${value}`}
      removeDataTestId={`tag-chip-remove-${value}`}
    />
  )
);

TagChip.displayName = 'TagChip';

export default TagChip;
